import {
    I18nIndividual as GedcomIndividual,
    IndividualBindings as DeserializedIndividual
} from "../model/gedcom-individual"
import {FamilyBindings as DeserializedFamily, I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {individualsHeader} from "../model/xlsx-individual-header"
import {familiesHeader} from "../model/xlsx-family-header"
import {
    ConverterError,
    DuplicatedId,
    DuplicatedParent,
    DuplicatedSpouse,
    InvalidDateFormat,
    InvalidId,
    MissingFamily,
    MissingHusband,
    MissingMandatoryField,
    MissingOneParent,
    MissingSpouse,
    MissingWife,
    ParentIsSelf, UnlinkedIndividual
} from "../error/converter-errors"
import {IntlShape} from "react-intl"
import {DateOrYear, InputMode} from "../datepicker/date-or-year"
import {validate as isUUID} from "uuid"


/*
 * WELCOME TO VALIDATION LAND 🤘🤘🤘 GRAB A DRINK FIRST
 *
 * This is the way things are, so far:
 *
 * INDIVIDUALS -> if ID column is missing, no need to continue
 *             -> validate remaining required columns
 * FAMILIES    -> if ID column is missing, no need to continue
 *             -> validate remaining required columns
 *
 * INDIVIDUOS -> validate individual ID format (Creator or UUID)
 * FAMILIES   -> validate family ID format (Creator or UUID)
 *
 * FAMILIES -> the family record must define both husband and wife
 *          -> both parents are different individuals
 *          -> if husband is defined but is not amongst the individuals, error
 *          -> otherwise map husband binding
 *          -> if wife is defined but is not amongst the individuals, error
 *          -> otherwise map wife binding
 *
 * INDIVIDUALS -> parents can be both unset or set, but not just one
 *             -> oneself can't be his/her own progenitor
 *             -> both parents must be different individuals
 *             -> if both parents exist as a family record, map bindings
 *
 * INDIVIDUALS -> validate that date columns achieved to deserialize as DateOrYear type
 * FAMILIES    -> validate that date columns achieved to deserialize as DateOrYear type
 *
 * FAMILIES -> traverse marriages
 *          -> if multiple relationships, flag all as divorced except the last one
 *
 * INDIVIDUALS -> validate that the deserialized bindings coincide with freshly calculated ones
 * FAMILIES    -> validate that the deserialized bindings coincide with freshly calculated ones
 *             -> otherwise, don't crash the import but send tampering suspicion report
 */

export const columnValidations = new Map<Function, string[]>([
    [GedcomIndividual, ["id", "sex"]],
    [GedcomFamily, ["id" /* husbandId, wifeId */ ]]  // Husband and wife already validated at validateFamilySpouses
])

export function validatePipeline(
    individualsData: GedcomIndividual[],
    familiesData: GedcomFamily[],
    intl: IntlShape,
    validateDisabled: boolean = true
) {
    // Rehydrate obj arrays
    individualsData = individualsData.map(i => new DeserializedIndividual(i))
    familiesData = familiesData.map(f => new DeserializedFamily(f))
    const validationErrors: ConverterError[] = []
    validateMandatoryColumns(GedcomIndividual, individualsData, validationErrors, intl)
    validateMandatoryColumns(GedcomFamily, familiesData, validationErrors, intl)
    validateIdsFormat(GedcomIndividual, individualsData, validationErrors, intl)
    validateIdsFormat(GedcomFamily, familiesData, validationErrors, intl)
    validateFamilySpouses(familiesData, individualsData, validationErrors, intl, validateDisabled)
    validateIndividualParents(individualsData, familiesData, validationErrors, intl, validateDisabled)
    validateDateFields(GedcomIndividual, individualsData, ["birthDate", "deceaseDate"], validationErrors, intl)
    validateDateFields(GedcomFamily, familiesData, ["marriageDate"], validationErrors, intl)
    validateUnlinked(individualsData, validationErrors, intl)
    mapDivorces(familiesData)
    return { validationErrors: validationErrors, validatedIndividuals: individualsData, validatedFamilies: familiesData }
}

/* ------------------------------------------------------------------------------------------------------------------ */

export const missingColMessages = new Map<Function, { id: string }>([
    [GedcomIndividual, { id: "converter.missing.individual.col" }],
    [GedcomFamily, { id: "converter.missing.family.col" }]
])

export const missingIdMessages = new Map<Function, { id: string }>([
    [GedcomIndividual, { id: "converter.missing.individual.id" }],
    [GedcomFamily, { id: "converter.missing.family.id" }]
])

function validateMandatoryColumns<T extends GedcomIndividual | GedcomFamily>(
    rowType: typeof GedcomIndividual | typeof GedcomFamily,
    rows: T[],
    validationErrors: ConverterError[],
    intl: IntlShape
) {
    rows.forEach((row, rowIndex) => {
        for (const col of columnValidations.get(rowType)!) {
            const value = (row as any)[col]
            const columnNames = rowType === GedcomIndividual ? individualsHeader(intl) : familiesHeader(intl)
            if (value === undefined || value === null || String(value).trim() === "") {
                if (col === "id") {
                    // If ID column is missing, no need to continue
                    validationErrors.push(new MissingMandatoryField(intl.formatMessage(
                        { id: missingIdMessages.get(rowType)!.id },
                        {
                            rowIndex: `<span style="font-weight: bold;" class="error-loc">${rowIndex + 2}</span>`,
                            col: `'<span style="font-weight: bold;">ID</span>'`
                        }), rowType, row.id, rowIndex + 2)
                    )
                    break
                } else
                    // validate remaining required columns
                    validationErrors.push(new MissingMandatoryField(intl.formatMessage(
                        { id: missingColMessages.get(rowType)!.id },
                        {
                            id: `[<span style="font-weight: bold;" class="error-loc">${row.id}</span>]`,
                            col: `'<span style="font-weight: bold;">${columnNames[col]}</span>'`
                        }), rowType, row.id, rowIndex + 2))
            }
        }
    })
}

const isValidId = (rowType: typeof GedcomIndividual | typeof GedcomFamily, id: string) =>
    new RegExp(`^${rowType.prefix}\\d{1,4}$`).test(id) || isUUID(id)

function validateIdsFormat<T extends GedcomIndividual | GedcomFamily>(
    rowType: typeof GedcomIndividual | typeof GedcomFamily,
    rows: T[],
    validationErrors: ConverterError[],
    intl: IntlShape
) {
    rows.filter(row => row.id).forEach((row, rowIndex) => {
        switch (rowType) {
            case GedcomIndividual:
                // Validate individual ID format (Creator or UUID)
                if (row.id && !isValidId(rowType, row.id))
                    validationErrors.push(new InvalidId(intl.formatMessage(
                        { id: "converter.invalid.individual.id" },
                        {
                            id: `[<span style="font-weight: bold;" class="error-loc">${row.id}</span>]`,
                            rowIndex: `<span style="font-weight: bold;">${rowIndex + 2}</span>`,
                            from: `[<span >${rowType.prefix}0</span>`, to: `<span>${rowType.prefix}9999</span>]`
                        }), rowType, row.id, rowIndex + 2))
                break
            case GedcomFamily:
                // Validate family ID format (Creator or UUID)
                if (row.id && !isValidId(rowType, row.id))
                    validationErrors.push(new InvalidId(intl.formatMessage({ id: "converter.invalid.family.id" }, {
                        id: `[<span style="font-weight: bold;" class="error-loc">${row.id}</span>]`,
                        rowIndex: `<span style="font-weight: bold;">${rowIndex + 2}</span>`,
                        from: `[<span>${rowType.prefix}0</span>`, to: `<span>${rowType.prefix}9999</span>]`
                    }), rowType, row.id, rowIndex + 2))
        }
    })
    validateDuplicatedIds(rowType, rows, validationErrors, intl)
}

function validateDuplicatedIds<T extends GedcomIndividual | GedcomFamily>(
    rowType: typeof GedcomIndividual | typeof GedcomFamily,
    rows: T[],
    validationErrors: ConverterError[],
    intl: IntlShape
) {
    const idCounts: Record<string, number> = {}
    // Check only valid ID's
    rows.forEach(row => {
        if (!row.id) return
        if (!isValidId(rowType, row.id)) return
        idCounts[row.id] = (idCounts[row.id] || 0) + 1
    })
    const literalKey = rowType.name.replace("Gedcom", "").toLowerCase()
    for (const [rowId, count] of Object.entries(idCounts))
        if (count > 1)
            validationErrors.push(new DuplicatedId(intl.formatMessage({
                id: `converter.duplicated.${literalKey}.id`,

            }, { rowId: `[<span style="font-weight: bold;" class="error-loc">${rowId}</span>]` }), rowType, rowId))
}

function validateFamilySpouses(families: GedcomFamily[], individuals: GedcomIndividual[], validationErrors: ConverterError[], intl: IntlShape, validateDisabled: boolean) {
    families.filter(f => f.enabled || !validateDisabled).forEach((family, rowIndex) => {

        // The family record must define both husband and wife
        if (!family.husbandId || !family.wifeId) {
            validationErrors.push(new MissingSpouse(
                intl.formatMessage({ id: "converter.missing.spouse"}, {
                familyId: `[<span style="font-weight: bold;" class="error-loc">${family.id}]</span>` }),
                GedcomFamily,
                family.id,
                rowIndex + 2)
            )
            return
        }

        // Both parents are different individuals
        if (family.husbandId === family.wifeId) {
            validationErrors.push(new DuplicatedSpouse(intl.formatMessage({ id: "converter.duplicated.spouse"}, {
                familyId: `[<span style="font-weight: bold;">${family.id}</span>]`,
                husbandId: `<span style="font-weight: bold;" class="error-loc">${family.husbandId}</span>`,
                wifeId: `<span style="font-weight: bold;" class="error-loc">${family.wifeId}</span>`
            }), GedcomFamily, family.id, rowIndex + 2))
            return
        }

        // If husband is defined but is not amongst the individuals, error
        const husband = individuals.find(ind => ind.id === family.husbandId)
        if (!husband || (!husband.enabled && validateDisabled)) {
            let message = intl.formatMessage({ id: "converter.missing.husband" }, {
                familyId:   `<span style="font-weight: bold;">${family.id}</span>`,
                husbandId1: `<span style="font-weight: bold;" class="error-loc">${family.husbandId}</span>`,
                wifeId:     `<span>${family.wifeId}</span>`,
                husbandId2: `<span style="font-weight: bold;">${family.husbandId}</span>`
            })
            message += husband && (!husband.enabled && validateDisabled)
                ? ` ${intl.formatMessage({ id: "converter.male.disabled" })}`
                : ` ${intl.formatMessage({ id: "converter.missing.nonexistent" })}`
            validationErrors.push(new MissingHusband(message, GedcomIndividual, family.id, rowIndex + 2))
            return
        } else {
            // Map bindings
            husband.familySpouses.add(family.id)
            husband!.numberOfMarriages = (husband!.numberOfMarriages ?? 0) + 1
        }

        // If wife is defined but is not amongst the individuals, error
        const wife = individuals.find(ind => ind.id === family.wifeId)
        if (!wife || (!wife.enabled  && validateDisabled)) {
            let message = intl.formatMessage({ id: "converter.missing.wife"}, {
                familyId:  `<span style="font-weight: bold;">${family.id}</span>`,
                husbandId: `<span>${family.husbandId}</span>`,
                wifeId1:   `<span style="font-weight: bold;" class="error-loc">${family.wifeId}</span>`,
                wifeId2:   `<span style="font-weight: bold;">${family.wifeId}</span>`
            })
            message += wife && (!wife.enabled && validateDisabled)
                ? ` ${intl.formatMessage({ id: "converter.female.disabled" })}`
                : ` ${intl.formatMessage({ id: "converter.missing.nonexistent" })}`
            validationErrors.push(new MissingWife(message, GedcomIndividual, family.id, rowIndex + 2))
            return
        }
        else {
            // Map bindings
            wife.familySpouses.add(family.id)
            wife!.numberOfMarriages = (wife!.numberOfMarriages ?? 0) + 1
        }
    })
}

function validateIndividualParents(individuals: GedcomIndividual[], families: GedcomFamily[], validationErrors: ConverterError[], intl: IntlShape, validateDisabled: boolean) {
    individuals.filter(indi => indi.id && indi.withParent()).forEach((indi, rowIndex) => {

        // Parents can be both unset or set, but not just one
        if (!indi.hasBothParents()) {
            validationErrors.push(new MissingOneParent(
                intl.formatMessage({ id: "converter.missing.parent"},
                {indiId: `[<span style="font-weight: bold;" class="error-loc">${indi.id}</span>]`}),
                GedcomIndividual, indi.id, rowIndex + 2)
            )
            return
        }

        // Oneself can't be his/her own progenitor
        if (indi.fatherId === indi.id || indi.motherId === indi.id) {
            validationErrors.push(new ParentIsSelf(
                intl.formatMessage({ id: "converter.parent.self"},
                {indiId: `[<span style="font-weight: bold;" class="error-loc">${indi.id}</span>]`}),
                GedcomIndividual, indi.id, rowIndex + 2)
            )
            return
        }

        // Both parents must be different individuals
        if (indi.fatherId === indi.motherId) {
            validationErrors.push(new DuplicatedParent(intl.formatMessage({id: "converter.duplicated.parent"},{
                indiId:   `[<span style="font-weight: bold;">${indi.id}</span>]`,
                fatherId: `<span style="font-weight: bold;" class="error-loc">${indi.fatherId}</span>`,
                motherId: `<span style="font-weight: bold;" class="error-loc">${indi.motherId}</span>`
            }), GedcomIndividual, indi.id, rowIndex + 2))
            return
        }

        // Both parents exist as a family record
        const family = families.find(fam => fam.husbandId === indi.fatherId && fam.wifeId === indi.motherId)
        if (!family || (!family.enabled && validateDisabled)) {
            let message = intl.formatMessage({ id: "converter.missing.family" }, {
                indiId:   `[<span style="font-weight: bold;">${indi.id}</span>]`,
                fatherId: `<span style="font-weight: bold;" class="error-loc">${indi.fatherId}</span>`,
                motherId: `<span style="font-weight: bold;" class="error-loc">${indi.motherId}</span>`
            })
            message += family && (!family.enabled && validateDisabled)
                ? ` ${intl.formatMessage({ id: "converter.family.disabled" })}`
                : ` ${intl.formatMessage({ id: "converter.missing.family.nonexistent" })}`
            validationErrors.push(new MissingFamily(message, GedcomFamily, indi.id, rowIndex + 2))
        } else {
            // Map bindings
            const husband = individuals.find(i => i.id === family.husbandId)
            if (husband) husband.numberOfChildren = (husband.numberOfChildren ?? 0) + 1
            const wife = individuals.find(i => i.id === family.wifeId)
            if (wife) wife.numberOfChildren = (wife.numberOfChildren ?? 0) + 1
            family.children.add(indi.id)
            indi.familyChild = family.id
        }
    })
}

export function validateDateFields<T extends GedcomIndividual | GedcomFamily>(
    rowType: typeof GedcomIndividual | typeof GedcomFamily,
    rows: T[],
    dateFields: (keyof GedcomIndividual | keyof GedcomFamily)[],
    validationErrors: ConverterError[],
    intl: IntlShape
) {
    const literalKey = rowType.name.replace("Gedcom", "").toLowerCase()
    dateFields.forEach((field) => rows.filter(row => row[field]).forEach((row, rowIndex) => {
        // Validate that date columns achieved to deserialize as DateOrYear type
        if(!(row[field] instanceof DateOrYear))
            validationErrors.push(new InvalidDateFormat(intl.formatMessage({ id: `converter.invalid.${literalKey}.date` }, {
                rowId:    `[<span style="font-weight: bold;" class="error-loc">${row.id}</span>]`,
                dateMask: `<span style="font-weight: bold;">${intl.formatMessage({ id: "date.mask" })}</span>`,
                dateYear: `<span style="font-weight: bold;">${intl.formatMessage({ id: "date.year" })}</span>`,
            }), rowType, row.id, rowIndex + 2))
    }))
}

const toTimestamp = (d: DateOrYear): number => d.mode === InputMode.DATE ? d.date.getTime() : new Date(d.year, 0, 1).getTime()

function validateUnlinked(individuals: GedcomIndividual[], validationErrors: ConverterError[], intl: IntlShape) {
    individuals.forEach((indi, rowIndex) => {
        // Must have spouse, parents or children
        const hasPartner = indi.familySpouses && indi.familySpouses.size > 0
        const hasChildren = indi.numberOfChildren && indi.numberOfChildren > 0
        const isChild = indi.familyChild
        if(!hasPartner && !hasChildren && !isChild) {
            validationErrors.push(new UnlinkedIndividual(
                intl.formatMessage({ id: "converter.unlinked.individual"},
                { indiId: `[<span style="font-weight: bold;" class="error-loc">${indi.id}</span>]` }),
                GedcomIndividual,
                indi.id,
                rowIndex + 2)
            )
            return
        }
    })
}

function mapDivorces(families: GedcomFamily[]): GedcomFamily[] {
    const personFamilies = families
        .filter(f => f.enabled && f.husbandId && f.wifeId)
        .flatMap(f => [{ id: f.husbandId!, family: f }, { id: f.wifeId!, family: f }])
    // Traverse marriages setting the divorces until the most recent relationship
    Object.values(groupBy(personFamilies, p => p.id))
        .filter(entries => entries.length > 1 && entries.every(e => e.family.marriageDate))
        .forEach(entries => {
            [...entries].sort((a, b) => toTimestamp(a.family.marriageDate!) - toTimestamp(b.family.marriageDate!))
                .slice(0, -1)
                .forEach(e => e.family.divorced = true)
        })
    return families
}

function groupBy<T, K extends string | number | symbol>(arr: T[], keyFn: (item: T) => K): Record<K, T[]> {
    return arr.reduce((acc, item) => {
        const key = keyFn(item);
        (acc[key] ||= []).push(item)
        return acc
    }, {} as Record<K, T[]>)
}